import type { FeatureCollection } from "geojson";

import { COUNTRY_BADGE_IMAGE } from "./countryBadge";

/** Frontières des pays fictifs de la fiche pays sur la carte du dossier (demande d'Alexis du 11/10/2026, seule
 *  exception à la carte figée) : fonctions pures du calque, testées par countryBorders.test.ts.
 *
 *  Le moteur (`borders_geojson`, servi par `GET …/country-sheet/borders`) donne des lignes `kind: "border"` entre
 *  deux pays (`subject` : frontière du pays de la fiche ; `theatre` : de l'État du théâtre), des points
 *  `kind: "label"` au nom de chaque pays (`role` : subject, theatre ou neighbor), et parfois les limites des régions
 *  du pays de la fiche (lignes `kind: "region"`) et ses villes (points `kind: "city"`, `capital`). */

/** Couche de la carte, dans la forme des composants `Layer` des moteurs (react-map-gl). */
export interface BorderLayerSpec {
  id: string;
  type: "line" | "symbol" | "circle";
  filter: unknown[];
  layout?: Record<string, unknown>;
  paint: Record<string, unknown>;
  maxzoom?: number;
}

/** Couleurs du calque (celles du rendu de la carte, posées par `CountryBordersLayers`) : rouge ennemi (APP-6) pour
 *  le pays de la fiche, gris foncé discret pour les autres, encre des villes, halo des libellés. */
export interface BorderColors {
  hostile: string;
  grey: string;
  ink: string;
  halo: string;
}

/** Couches du calque sous les graphiques tactiques (`beforeId`) : au-dessus du fond et des couches du camp, sous
 *  les mesures de coordination et les unités. */
export const BORDERS_BELOW = "graphics-fill";

/** Régions et villes : aux petites échelles seulement, pour ne pas encombrer la carte tactique. */
export const DETAIL_MAXZOOM = 8;

const FONT = ["Open Sans Semibold"];
const FONT_LIGHT = ["Open Sans Regular", "Arial Unicode MS Regular"];

const kind = (value: string) => ["==", ["get", "kind"], value];

/** Couches du calque, de bas en haut : limites des régions (tiretés fins), autres frontières (gris), frontières du
 *  pays de la fiche (rouge ennemi), villes (petites échelles), étiquettes des noms des pays (blanc sur rouge). */
export function countryBorderLayers(colors: BorderColors): BorderLayerSpec[] {
  const subject = ["==", ["get", "subject"], true];
  return [
    {
      // Limites des régions du pays de la fiche : tiretés fins, aux petites échelles seulement.
      id: "country-regions",
      type: "line",
      filter: kind("region"),
      maxzoom: DETAIL_MAXZOOM,
      layout: { "line-join": "round" },
      paint: { "line-color": colors.grey, "line-opacity": 0.45, "line-width": 0.8, "line-dasharray": [3, 3] },
    },
    {
      // Autres frontières : gris foncé en tiretés longs, lisible sans se confondre avec les limites du fond.
      id: "country-borders",
      type: "line",
      filter: ["all", kind("border"), ["!", subject]],
      layout: { "line-join": "round", "line-cap": "butt" },
      paint: {
        "line-color": colors.grey,
        "line-opacity": 0.85,
        "line-width": ["interpolate", ["linear"], ["zoom"], 3, 1.2, 8, 1.8, 12, 2.2],
        "line-dasharray": [6, 3],
      },
    },
    {
      // Liseré blanc sous la frontière du pays de la fiche : elle se détache de tous les fonds de carte.
      id: "country-borders-subject-casing",
      type: "line",
      filter: ["all", kind("border"), subject],
      layout: { "line-join": "round", "line-cap": "round" },
      paint: {
        "line-color": colors.halo,
        "line-opacity": 0.85,
        "line-width": ["interpolate", ["linear"], ["zoom"], 3, 4, 8, 5.5, 12, 6.5],
      },
    },
    {
      id: "country-borders-subject",
      type: "line",
      filter: ["all", kind("border"), subject],
      layout: { "line-join": "round", "line-cap": "round" },
      paint: {
        "line-color": colors.hostile,
        "line-opacity": 0.95,
        "line-width": ["interpolate", ["linear"], ["zoom"], 3, 2.2, 8, 3.2, 12, 4],
      },
    },
    {
      // Villes : un point cerclé, la capitale un peu plus grande.
      id: "country-cities",
      type: "circle",
      filter: kind("city"),
      maxzoom: DETAIL_MAXZOOM,
      paint: {
        "circle-radius": ["case", ["==", ["get", "capital"], true], 3.5, 2.5],
        "circle-color": colors.halo,
        "circle-stroke-color": colors.ink,
        "circle-stroke-width": 1,
      },
    },
    {
      id: "country-city-names",
      type: "symbol",
      filter: kind("city"),
      maxzoom: DETAIL_MAXZOOM,
      layout: {
        "text-field": ["get", "name"],
        "text-font": FONT_LIGHT,
        "text-size": 10,
        "text-anchor": "left",
        "text-offset": [0.6, 0],
        "text-optional": true,
      },
      paint: { "text-color": colors.ink, "text-halo-color": colors.halo, "text-halo-width": 1.2 },
    },
    {
      // Noms des pays (demande d'Alexis du 11/10/2026) : étiquettes en texte blanc sur fond rouge, au centre de
      // chaque pays et, de part et d'autre de la frontière la plus proche de la zone d'opérations, au plus près
      // de l'exercice (`near`) ; le pays de la fiche d'abord, puis l'État du théâtre.
      id: "country-names",
      type: "symbol",
      filter: kind("label"),
      layout: {
        "text-field": ["upcase", ["get", "name"]],
        "text-font": FONT,
        "text-size": ["interpolate", ["linear"], ["zoom"], 3, 10.5, 8, 12, 12, 13],
        "text-letter-spacing": 0.12,
        "text-max-width": 30,
        "icon-image": COUNTRY_BADGE_IMAGE,
        "icon-text-fit": "both",
        "icon-text-fit-padding": [3, 7, 3, 7],
        "symbol-sort-key": ["match", ["get", "role"], "subject", 0, "theatre", 1, 2],
      },
      paint: { "text-color": colors.halo },
    },
  ];
}

/** Calque sans dossier ouvert (`off`), sans fiche pays (`none`), pendant sa lecture (`loading`), ou prêt. */
export type BordersState = "off" | "none" | "loading" | "ready";

/** État du calque d'après la lecture de la fiche pays : pas de dossier ouvert → « off » (entrée grisée) ; fiche
 *  absente (404) → « none » (l'entrée désigne « Rédiger la fiche pays ») ; fiche lue → « ready » ; lecture en cours
 *  ou en échec passager → « loading » (entrée grisée le temps de la relecture, jamais désignée à tort). */
export function bordersState(sheet: {
  exerciseId: string | null;
  hasData: boolean;
  missing: boolean;
}): BordersState {
  if (!sheet.exerciseId) return "off";
  if (sheet.missing) return "none";
  return sheet.hasData ? "ready" : "loading";
}

/** Nombre de pays nommés sur la carte (compteur de l'entrée des Calques). */
export function countryCount(borders: FeatureCollection | undefined): number {
  return (borders?.features ?? []).filter((f) => (f.properties as { kind?: unknown } | null)?.kind === "label").length;
}
